import GraphQLService from './GraphQLService'
import { removeExtraSpaces } from '../common/helpers'
import { loggedGraphqlQuery } from './loggedGraphqlQuery'
import { mapTrustBundleGql } from './mappers/mapTrustBundleGql'
import {
  TRUST_MARKER_FIELDS,
  TRUST_MARKERS_COMBINATION_FIELDS,
} from './trustBundle/graphqlFields'
import { cachedSsrFetch } from '../common/ssrContentfulCache'
import { processTrustMarkersCombination } from '../common/processTrustMarkersCombination'
import {
  trustMarkerEntryId,
  trustMarkerEntryLink,
  trustMarkerEntryRatingNumber,
  trustMarkerEntryTotalReviewNumber,
} from '../common/trustMarkerEntryFields'

const CACHE_KEY = 'trustBundleGql'

function buildPageBundleFromRaw(
  { combinations, markers, reviews },
  combinationId = 'home-variant-8'
) {
  let trustMarkersCombination = combinations.find(
    (item) => item.id?.toLowerCase() === combinationId.toLowerCase()
  )

  if (trustMarkersCombination) {
    trustMarkersCombination = {
      ...trustMarkersCombination,
      trustMarker: trustMarkersCombination.trustMarker.map((item) => {
        const baseFields = item.fields ?? item

        return {
          metadata: item.metadata ?? null,
          sys: item.sys ?? null,
          fields: {
            ...baseFields,
            color: '#33b083',
          },
        }
      }),
    }
  }

  const trustPilotEntry = markers.find((item) => {
    const id = trustMarkerEntryId(item)?.toLowerCase()

    return id === 'truspilot' || id === 'trustpilot'
  })
  const tpRating = trustMarkerEntryRatingNumber(trustPilotEntry)
  const tpTotal = trustMarkerEntryTotalReviewNumber(trustPilotEntry)

  return {
    trustMarkersCombination,
    trustPilotInfo: {
      rating: Number.isFinite(tpRating) ? tpRating : 0,
      link: trustMarkerEntryLink(trustPilotEntry) ?? '',
      totalReview: Number.isFinite(tpTotal) ? tpTotal : 0,
    },
    reviews,
  }
}

export default class TrustBundleService extends GraphQLService {
  async fetchRaw(preview = false) {
    const query = removeExtraSpaces(`
      query TrustBundle($preview: Boolean) {
        combinations: trustMarkersCombinationCollection(preview: $preview) {
          items { ${TRUST_MARKERS_COMBINATION_FIELDS} }
        }
        markers: trustMarkerCollection(preview: $preview, order: id_ASC) {
          items { ${TRUST_MARKER_FIELDS} }
        }
        reviews: reviewsCollection(
          preview: $preview
          limit: 1
          where: { slug: "trustpilot" }
        ) {
          items {
            slug
            title
            values
            sys { id }
          }
        }
      }
    `)

    const data = await loggedGraphqlQuery(
      'TrustBundleService.fetchRaw',
      () => this.run(query, preview),
      {
        context: { preview },
        countItems: (d) => d?.markers?.items?.length ?? null,
      }
    )

    return mapTrustBundleGql(data)
  }

  /** Cached raw bundle (combinations, markers, reviews). */
  async getRawBundle(preview = false) {
    return cachedSsrFetch(CACHE_KEY, () => this.fetchRaw(preview), { preview })
  }

  /** All trust markers ordered by id (replaces TrustMarkerRepository.getModels). */
  async getTrustMarkers(preview = false) {
    const { markers } = await this.getRawBundle(preview)

    return markers
  }

  /** All trust marker combinations (replaces TrustMarkersCombinationRepository.getModels). */
  async getCombinations(preview = false) {
    const { combinations } = await this.getRawBundle(preview)

    return combinations
  }

  async getCombinationById(combinationId, preview = false) {
    const combinations = await this.getCombinations(preview)

    return (
      combinations.find(
        (item) => item.id?.toLowerCase() === combinationId?.toLowerCase()
      ) ?? null
    )
  }

  /** Combination with #33b083 color injected on each marker (header/footer strip). */
  async getCombinationWithColors(combinationId, preview = false) {
    const combination = await this.getCombinationById(combinationId, preview)

    if (!combination?.trustMarker?.length) {
      return combination
    }

    return {
      ...combination,
      trustMarker: combination.trustMarker.map((item) => {
        const baseFields = item.fields ?? item

        return {
          metadata: item.metadata ?? null,
          sys: item.sys ?? null,
          fields: {
            ...baseFields,
            color: '#33b083',
          },
        }
      }),
    }
  }

  async getPageBundle(preview = false, combinationId = 'home-variant-8') {
    const raw = await this.getRawBundle(preview)

    return buildPageBundleFromRaw(raw, combinationId)
  }

  /**
   * Single cached payload for _app + page GSSP dedupe.
   * @param {boolean} preview
   * @param {string} [footerCombinationId]
   */
  async getFullSsrPayload(
    preview = false,
    footerCombinationId = 'home-variant-8'
  ) {
    const raw = await this.getRawBundle(preview)

    const footerMatch = raw.combinations.find(
      (item) => item.id?.toLowerCase() === footerCombinationId.toLowerCase()
    )

    return {
      markers: raw.markers,
      combinations: raw.combinations,
      reviews: raw.reviews,
      footerCombination: processTrustMarkersCombination(footerMatch),
      pageBundle: buildPageBundleFromRaw(raw),
    }
  }
}
